import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, AlertOctagon, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export function ExceptionCenter() {
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from('integrity_exceptions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) {
      toast.error('Failed to load exceptions');
    } else {
      setExceptions(data || []);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const statusColor = (s: string) => {
    switch (s) {
      case 'OPEN':
        return 'destructive';
      case 'APPROVED':
        return 'default';
      case 'RESOLVED':
        return 'secondary';
      case 'REJECTED':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const severityColor = (s: string) => {
    switch (s) {
      case 'critical':
        return 'text-destructive';
      case 'high':
        return 'text-amber-600';
      case 'medium':
        return 'text-amber-500';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <AlertOctagon className="h-5 w-5 text-primary" />
          Exception Center
        </h2>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['OPEN', 'APPROVED', 'RESOLVED', 'REJECTED'].map((status) => (
          <Card key={status}>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">{status}</div>
              <div className="text-2xl font-bold mt-1">
                {exceptions.filter((e) => e.status === status).length}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Integrity Exceptions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Check</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exceptions.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">{e.check_name}</TableCell>
                    <TableCell>
                      <span className={`capitalize font-medium ${severityColor(e.severity)}`}>
                        {e.severity}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusColor(e.status) as any}>{e.status}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate text-sm text-muted-foreground">
                      {e.reason}
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(e.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
                {exceptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No exceptions recorded
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
